import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, Download } from "lucide-react";
import Question from "./Question";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import QuizImportTemplate from "../../../../assets/Quiz MCQ Import Template.xlsx";
import { Trash2 } from "lucide-react";
import ConfirmDialogBox from "../../../confirmdiolog/ConfirmDiologBox";
import QuizService from "../../../../services/QuizService";


const generateId = () => "Q_" + Math.random().toString(36).substring(2, 9);

const createEmptyQuestion = (order) => ({
  id: generateId(),
  serverId: null,
  question: "",
  orderBy: order,
  hasGraceMark: false,
  options: [
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 1,
    },
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 2,
    },
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 3,
    },
    {
      id: generateId(),
      serverId: null,
      option: "",
      isAnswer: false,
      orderBy: 4,
    },
  ],
});

const QuizQuestions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const authContext = useContext(AuthContext);
  const [showDeleteQuizDialog, setShowDeleteQuizDialog] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [toBeDeleted, setToBeDeleted] = useState([]);
  useEffect(() => {
    fetchExistingQuestions();
  }, [id]);

  const fetchExistingQuestions = async () => {
    try {
      const res = await QuizService.GetAllQuizzes(id, authContext);

      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];

      if (list.length > 0) {
        const mapped = list.map((q, qi) => ({
          id: generateId(),
          serverId: q.id,
          question: q.question,
          orderBy: q.orderBy ?? qi + 1,
          hasGraceMark: q.hasGraceMark ?? false,
          options: [...q.options]
            .sort((a, b) => a.orderBy - b.orderBy)
            .map((o, oi) => ({
              id: generateId(),
              serverId: o.id,
              option: o.option,
              isAnswer: o.isAnswer,
              orderBy: o.orderBy ?? oi + 1,
            })),
        }));

        setQuestions(mapped);
      } else {
        setQuestions([createEmptyQuestion(1)]);
      }
    } catch (err) {
      console.error("Failed to fetch quiz questions", err);
      setQuestions([createEmptyQuestion(1)]);
    }
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, createEmptyQuestion(prev.length + 1)]);
  };

  const removeQuestion = (qid) => {
    setQuestions((prev) => {
      const removed = prev.find((q) => q.id === qid);

      if (removed?.serverId) {
        setToBeDeleted((d) => [...d, removed.serverId]);
      }

      return prev
        .filter((q) => q.id !== qid)
        .map((q, i) => ({ ...q, orderBy: i + 1 }));
    });
  };

  const updateQuestion = (qid, patch) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === qid ? { ...q, ...patch } : q)),
    );
  };

  const submitHandler = async () => {
    try {
      const payload = {
        toBeDeleted: toBeDeleted,
        models: questions.map((q) => ({
          ...(q.serverId && { id: q.serverId }),
          videoId: id,
          question: q.question,
          hasGraceMark: q.hasGraceMark,
          isAnswered: false,
          orderBy: q.orderBy,
          options: q.options.map((o) => ({
            ...(o.serverId && { id: o.serverId }),
            ...(q.serverId && { questionId: q.serverId }),
            orderBy: o.orderBy,
            option: o.option,
            isAnswer: o.isAnswer,
          })),
        })),
      };

      const res = await QuizService.PostBulkQuiz(payload, authContext);

      const response = res?.data?.isSuccess !== undefined ? res.data : res;

      if (response.isSuccess) {
        AlertService.success("Quiz questions uploaded successfully");
        navigate(-1);
      } else {
        AlertService.error(
          response.errorMessage || "Failed to upload quiz questions",
        );
      }
    } catch (err) {
      console.error("Quiz submit failed", err);
      AlertService.error("Failed to upload quiz questions");
    }
  };

const confirmDeleteQuiz = async () => {
  try {
    const questionIds = questions
      .filter(q => q.serverId)
      .map(q => q.serverId);
    if (questionIds.length > 0) {
      await QuizService.PostBulkQuiz(
        {
          toBeDeleted: questionIds,
          models: [],
        },
        authContext
      );
    }
    await QuizService.DeleteQuiz(id, authContext);

    AlertService.success("Quiz removed successfully");
    navigate(-1);
  } catch (err) {
    console.error(err);
    AlertService.error("Failed to remove quiz");
  }
};



  return (
    <>
    <div className="w-full py-6">
    <div className="flex justify-between items-center mb-6">

  <button
    onClick={() => setShowDeleteQuizDialog(true)}
      className="
    flex items-center gap-2
    px-4 py-2
    rounded-md
    border border-red-500
    text-red-600

    hover:bg-red-50

    dark:border-red-400
    dark:text-red-400
    dark:hover:bg-red-500/10
  "
  >
    <Trash2 size={18} />
    Delete Quiz
  </button>


  <div className="flex gap-6 text-base">
    <button
      className="flex items-center gap-2 underline hover:text-blue-600"
      onClick={() => navigate(`/masters-quizzes/${id}/questions/import`)}
    >
      <Upload size={20} />
      Upload Questions
    </button>

    <a
      href={QuizImportTemplate}
      download="Quiz MCQ Import Template.xlsx"
      className="
        flex items-center gap-2 underline
        text-gray-800 hover:text-blue-600
        dark:text-gray-300 dark:hover:text-blue-400
      "
    >
      <Download size={24} />
      Download Template
    </a>
  </div>
</div>

      {/* Questions */}
      <div className="space-y-6">
        {questions.map((q, i) => (
          <Question
            key={q.id}
            index={i}
            input={q}
            onChange={updateQuestion}
            onRemove={removeQuestion}
          />
        ))}
      </div>

      {/* Bottom actions */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <button
          onClick={addQuestion}
          className="sm:mr-auto px-6 py-3 rounded-md bg-[#0e3a46] text-white text-base"
        >
          Add Question
        </button>

        <div className="flex gap-4">
          <button
            onClick={() => navigate(-1)}
            className="
              px-8 py-2 rounded-md
              border border-gray-400
              hover:bg-gray-200
              dark:hover:bg-slate-800
            "
          >
            Cancel
          </button>

          <button
            onClick={submitHandler}
            className="px-8 py-2 rounded-md bg-[#0e3a46] text-white text-base"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
    <ConfirmDialogBox
  visible={showDeleteQuizDialog}
  onHide={() => setShowDeleteQuizDialog(false)}
  onConfirm={confirmDeleteQuiz}
  title={
    <>
      Are you sure you want to remove this <strong>quiz</strong>? <br />
    </>
  }
  confirmLabel="Delete"
  cancelLabel="Cancel"
/>

    </>
  );
};

export default QuizQuestions;
